import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Grid,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Divider,
  IconButton,
  Collapse,
  CircularProgress,
} from "@mui/material";
import {
  Send,
  Preview,
  ExpandMore,
  ExpandLess,
  Search,
  Notifications,
  NotificationsActive,
} from "@mui/icons-material";
import "./BulkPushNotification.css";
import {
  useGetUsersWithPushSubscriptionsQuery,
  useSendBulkPushNotificationMutation,
  useGetPushNotificationStatsQuery,
} from "state/api";

function BulkPushNotification() {
  const [notificationTitle, setNotificationTitle] = useState("");
  const [notificationBody, setNotificationBody] = useState("");
  const [notificationIcon, setNotificationIcon] = useState("");
  const [notificationUrl, setNotificationUrl] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    role: "all",
    status: "all",
  });
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sendConfirmOpen, setSendConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [filtersExpanded, setFiltersExpanded] = useState(true);

  // API hooks
  const {
    data: usersData = {},
    isLoading,
    error,
    refetch,
  } = useGetUsersWithPushSubscriptionsQuery(filters);

  const users = usersData.users || [];

  const { data: pushStats, isLoading: isStatsLoading } =
    useGetPushNotificationStatsQuery();

  const [sendBulkPushNotification, { isLoading: isSending }] =
    useSendBulkPushNotificationMutation();

  // Refetch users when filters change
  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  // Filter users based on selected filters and search term
  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      !searchTerm ||
      user.pseudo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.userLastName?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map((user) => user._id));
    }
  };

  const handleSendNotification = async () => {
    try {
      const response = await sendBulkPushNotification({
        title: notificationTitle,
        body: notificationBody,
        icon: notificationIcon || "/logo_ita.ico",
        url: notificationUrl || "https://infotraficalgerie.com",
        userIds: selectedUsers,
      }).unwrap();

      setSnackbar({
        open: true,
        message:
          response.message ||
          `Notification envoyée avec succès à ${selectedUsers.length} utilisateur(s)`,
        severity: "success",
      });

      // Reset form
      setNotificationTitle("");
      setNotificationBody("");
      setNotificationIcon("");
      setNotificationUrl("");
      setSelectedUsers([]);
      setSendConfirmOpen(false);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.data?.error || "Erreur lors de l'envoi de la notification",
        severity: "error",
      });
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      {/* Header */}
      <div style={{ marginBottom: "24px" }}>
        <Typography
          variant="h4"
          style={{ fontWeight: 600, marginBottom: "8px" }}
        >
          Envoi de notifications push en masse
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Envoyez des notifications push à plusieurs utilisateurs en même temps
        </Typography>

        {/* Push notification stats */}
        {!isStatsLoading && pushStats && (
          <Box sx={{ mt: 2 }}>
            <Paper sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
              <Typography variant="body2" color="textSecondary">
                📊 Statistiques des abonnements push: {pushStats.active} actifs
                ({pushStats.withUsers} liés à des utilisateurs,{" "}
                {pushStats.anonymous} anonymes)
              </Typography>
            </Paper>
          </Box>
        )}
      </div>

      {error && (
        <Alert severity="error" style={{ marginBottom: "24px" }}>
          Erreur lors du chargement des utilisateurs:{" "}
          {error?.data?.message || error.message}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Notification Composition */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" style={{ marginBottom: "16px" }}>
                <NotificationsActive sx={{ mr: 1, verticalAlign: "middle" }} />
                Composer la notification
              </Typography>

              <TextField
                fullWidth
                label="Titre de la notification"
                value={notificationTitle}
                onChange={(e) => setNotificationTitle(e.target.value)}
                style={{ marginBottom: "16px" }}
                required
                helperText="Le titre principal de votre notification"
              />

              <TextField
                fullWidth
                label="Message de la notification"
                value={notificationBody}
                onChange={(e) => setNotificationBody(e.target.value)}
                multiline
                rows={4}
                style={{ marginBottom: "16px" }}
                required
                helperText="Le contenu principal de votre notification"
              />

              <TextField
                fullWidth
                label="URL de l'icône (optionnel)"
                value={notificationIcon}
                onChange={(e) => setNotificationIcon(e.target.value)}
                style={{ marginBottom: "16px" }}
                placeholder="/logo_ita.ico"
                helperText="URL de l'icône à afficher (optionnel, logo par défaut si vide)"
              />

              <TextField
                fullWidth
                label="URL de destination (optionnel)"
                value={notificationUrl}
                onChange={(e) => setNotificationUrl(e.target.value)}
                style={{ marginBottom: "24px" }}
                placeholder="https://infotraficalgerie.com"
                helperText="URL vers laquelle rediriger quand l'utilisateur clique (optionnel)"
              />

              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  variant="outlined"
                  startIcon={<Preview />}
                  onClick={() => setPreviewOpen(true)}
                  disabled={!notificationTitle || !notificationBody}
                >
                  Aperçu
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => setSendConfirmOpen(true)}
                  disabled={
                    !notificationTitle ||
                    !notificationBody ||
                    selectedUsers.length === 0
                  }
                >
                  Envoyer à {selectedUsers.length} utilisateur(s)
                </Button>
              </div>
            </CardContent>
          </Card>
        </Grid>

        {/* User Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <Typography variant="h6">Sélection d'utilisateurs</Typography>
                <IconButton
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                >
                  {filtersExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </div>

              <Collapse in={filtersExpanded}>
                <div style={{ marginBottom: "24px" }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Rôle</InputLabel>
                        <Select
                          value={filters.role}
                          label="Rôle"
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              role: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="all">Tous les rôles</MenuItem>
                          <MenuItem value="user">Utilisateur</MenuItem>
                          <MenuItem value="moderator">Modérateur</MenuItem>
                          <MenuItem value="admin">Administrateur</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Statut</InputLabel>
                        <Select
                          value={filters.status}
                          label="Statut"
                          onChange={(e) =>
                            setFilters((prev) => ({
                              ...prev,
                              status: e.target.value,
                            }))
                          }
                        >
                          <MenuItem value="all">Tous les statuts</MenuItem>
                          <MenuItem value="active">Actif</MenuItem>
                          <MenuItem value="inactive">Inactif</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </div>
              </Collapse>

              <Divider style={{ marginBottom: "16px" }} />

              {/* Search Bar */}
              <TextField
                fullWidth
                size="small"
                placeholder="Rechercher par nom, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search style={{ marginRight: "8px", color: "#999" }} />
                  ),
                }}
                style={{ marginBottom: "16px" }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "16px",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  {isLoading
                    ? "Chargement..."
                    : `${filteredUsers.length} utilisateur(s) trouvé(s)`}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSelectAll}
                  disabled={isLoading}
                >
                  {selectedUsers.length === filteredUsers.length
                    ? "Désélectionner tout"
                    : "Sélectionner tout"}
                </Button>
              </div>

              <Paper
                variant="outlined"
                style={{ maxHeight: "300px", overflow: "auto" }}
              >
                {isLoading ? (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      padding: "24px",
                    }}
                  >
                    <CircularProgress />
                  </div>
                ) : (
                  <List dense>
                    {filteredUsers.map((user) => (
                      <ListItem
                        key={user._id}
                        button
                        onClick={() => handleUserToggle(user._id)}
                      >
                        <Checkbox
                          checked={selectedUsers.includes(user._id)}
                          tabIndex={-1}
                          disableRipple
                          sx={{
                            color: selectedUsers.includes(user._id)
                              ? "#4caf50"
                              : "default",
                            "&.Mui-checked": {
                              color: "#4caf50",
                            },
                          }}
                        />
                        <ListItemText
                          primary={
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              {user.pseudo}
                              {user.userReceiveNotifications && (
                                <Notifications
                                  sx={{
                                    ml: 1,
                                    fontSize: 16,
                                    color: "#4caf50",
                                  }}
                                />
                              )}
                            </div>
                          }
                          secondary={
                            <div>
                              <Typography variant="body2" color="textSecondary">
                                {user.email}
                              </Typography>
                              <div style={{ marginTop: "4px" }}>
                                <Chip
                                  label={user.role}
                                  size="small"
                                  variant="outlined"
                                  style={{
                                    marginRight: "8px",
                                    fontSize: "0.7rem",
                                  }}
                                />
                                <Chip
                                  label={user.status || "active"}
                                  size="small"
                                  color={
                                    (user.status || "active") === "active"
                                      ? "success"
                                      : "default"
                                  }
                                  style={{ fontSize: "0.7rem" }}
                                />
                              </div>
                            </div>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Aperçu de la notification</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginBottom: "8px",
              }}
            >
              <img
                src={notificationIcon || "/logo_ita.ico"}
                alt="Icon"
                style={{ width: 24, height: 24, marginRight: 8 }}
                onError={(e) => {
                  e.target.src = "/logo_ita.ico";
                }}
              />
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                {notificationTitle}
              </Typography>
            </div>
            <Typography variant="body2" color="textSecondary">
              {notificationBody}
            </Typography>
            {notificationUrl && (
              <Typography
                variant="caption"
                color="primary"
                style={{ marginTop: "8px", display: "block" }}
              >
                🔗 {notificationUrl}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Send Confirmation Dialog */}
      <Dialog open={sendConfirmOpen} onClose={() => setSendConfirmOpen(false)}>
        <DialogTitle>Confirmer l'envoi</DialogTitle>
        <DialogContent>
          <Typography>
            Êtes-vous sûr de vouloir envoyer cette notification à{" "}
            {selectedUsers.length} utilisateur(s) ?
          </Typography>
          <div style={{ marginTop: "16px" }}>
            <Typography variant="body2" color="textSecondary">
              Titre: {notificationTitle}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Message: {notificationBody}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Destinataires: {selectedUsers.length} utilisateur(s)
            </Typography>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setSendConfirmOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={handleSendNotification}
            variant="contained"
            disabled={isSending}
          >
            {isSending ? "Envoi en cours..." : "Confirmer l'envoi"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default BulkPushNotification;
